/**
 * AddActionAffordance — the CLOSED multi-action gate, made visible (AC-7).
 *
 * Disabled with a stated reason, never hidden: the shape is real (the contract
 * declares a LIST) and the gate is deliberate, so an AM who wonders "can I add
 * the second mortgagee here?" gets an answer instead of an absence (PRD UI/UX).
 *
 * The gate source is `binding.maxActions` — read from the declared schema's
 * `maxItems`, the same source the contract validates against, and treated as
 * CLOSED when `maxItems` is absent (PAD-3). The form introduces NO second gate:
 * no feature flag, no App Config key, no local constant (design Constraint 22).
 *
 * The reason is RENDERED TEXT wired through `aria-describedby`, not a tooltip
 * and not conveyed by the disabled styling alone — a disabled control whose
 * reason only exists visually is unreadable to a screen reader.
 */

import { useId } from "react";

export interface AddActionAffordanceProps {
  /** `binding.maxActions`. Enabled only when the declared gate is > 1. */
  maxActions: number;
  /** How many actions the form currently holds. */
  actionCount: number;
  /** Only called when the gate is open. */
  onAdd?: () => void;
  disabled?: boolean;
}

export const CLOSED_GATE_REASON =
  "N per policy eventually. Pinned to one; the list is already in the contract.";

export default function AddActionAffordance({
  maxActions,
  actionCount,
  onAdd,
  disabled = false,
}: AddActionAffordanceProps) {
  const reasonId = `add-action-reason-${useId().replace(/:/g, "")}`;
  const gateOpen = maxActions > 1 && actionCount < maxActions;
  const reason = gateOpen
    ? null
    : maxActions > 1
      ? `This launch already carries the maximum of ${maxActions} actions.`
      : CLOSED_GATE_REASON;

  return (
    <div
      className="flex flex-col gap-0.5"
      data-testid="launch-add-action-affordance"
    >
      <button
        type="button"
        disabled={!gateOpen || disabled}
        aria-describedby={reason ? reasonId : undefined}
        onClick={gateOpen ? onAdd : undefined}
        className="flex touch-target w-full items-center justify-center gap-2 rounded-lg border border-dashed border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-1.5 text-xs font-medium text-primary-gray-light-400 disabled:opacity-100"
        data-testid="launch-add-action"
      >
        <span aria-hidden="true">+</span>
        Add another mortgagee action
      </button>
      {reason ? (
        <p
          id={reasonId}
          className="text-2xs leading-snug text-primary-gray-light-400"
          data-testid="launch-add-action-reason"
        >
          {reason}
        </p>
      ) : null}
    </div>
  );
}
